<template>
    <div
        v-if="show"
        class="fixed inset-0 z-50 flex items-center justify-center"
    >
        <div
            class="absolute inset-0 bg-slate-900/50"
            @click="$emit('update:show', false)"
        ></div>
        <div
            class="relative bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-sm md:max-w-lg mx-4 p-6"
        >
            <div class="flex items-center justify-between mb-6">
                <h3 class="text-xl font-medium text-slate-900 dark:text-slate-100">
                    Edit billing address
                </h3>
                <button
                    class="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    <Icon icon="close" class="w-5 h-5 text-slate-500 dark:text-slate-400" />
                </button>
            </div>

            <div class="flex flex-col gap-4">
                <div>
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        Country
                    </label>
                    <select
                        v-model="addressForm.country"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange bg-white dark:bg-slate-800"
                    >
                        <option value="">Select country</option>
                        <option
                            v-for="c in countries"
                            :key="c.code"
                            :value="c.code"
                        >
                            {{ c.name }}
                        </option>
                    </select>
                </div>

                <div>
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        Company name
                        <span class="text-slate-400 font-normal">
                            (optional)
                        </span>
                    </label>
                    <input
                        v-model="addressForm.company"
                        type="text"
                        placeholder="Company name"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                    />
                </div>

                <div>
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        Address Line 1
                    </label>
                    <input
                        v-model="addressForm.line1"
                        type="text"
                        placeholder="Street address"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                    />
                </div>

                <div>
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        Address Line 2
                        <span class="text-slate-400 font-normal">
                            (optional)
                        </span>
                    </label>
                    <input
                        v-model="addressForm.line2"
                        type="text"
                        placeholder="Appartment, Suite etc"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                    />
                </div>

                <div>
                    <label
                        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                    >
                        City
                    </label>
                    <input
                        v-model="addressForm.city"
                        type="text"
                        placeholder="Enter city"
                        class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                    />
                </div>

                <div class="flex gap-4">
                    <div class="flex-1">
                        <label
                            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                        >
                            State
                        </label>
                        <input
                            v-model="addressForm.state"
                            type="text"
                            placeholder="Select state"
                            class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                        />
                    </div>
                    <div class="flex-1">
                        <label
                            class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1.5"
                        >
                            ZIP code
                        </label>
                        <input
                            v-model="addressForm.postal_code"
                            type="text"
                            placeholder="12345"
                            class="w-full px-3 py-2.5 border border-slate-200 dark:border-slate-700 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange focus:border-orange"
                        />
                    </div>
                </div>
            </div>

            <div v-if="addressError" class="text-sm text-red-500 mt-4">
                {{ addressError }}
            </div>

            <div class="flex justify-end gap-3 mt-6">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-slate-100 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                    @click="$emit('update:show', false)"
                >
                    Cancel
                </button>
                <button
                    class="px-4 py-2.5 bg-slate-900 text-sm font-medium text-white rounded shadow-sm hover:bg-slate-800 transition-colors disabled:opacity-50"
                    :disabled="addressProcessing"
                    @click="handleSaveBillingAddress"
                >
                    {{ addressProcessing ? 'Saving...' : 'Save changes' }}
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    billingAddress: {
        type: Object,
        default: () => ({}),
    },
});

const emit = defineEmits(['update:show', 'address-saved']);

const addressProcessing = ref(false);
const addressError = ref('');
const addressForm = ref({
    country: '',
    company: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
});

const countries = [
    { code: 'AU', name: 'Australia' },
    { code: 'CA', name: 'Canada' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'JP', name: 'Japan' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
];

watch(
    () => props.show,
    (val) => {
        if (val) {
            addressForm.value = {
                country: props.billingAddress.country || '',
                company: props.billingAddress.company || '',
                line1: props.billingAddress.line1 || '',
                line2: props.billingAddress.line2 || '',
                city: props.billingAddress.city || '',
                state: props.billingAddress.state || '',
                postal_code: props.billingAddress.postal_code || '',
            };
            addressError.value = '';
        }
    }
);

const handleSaveBillingAddress = async () => {
    addressProcessing.value = true;
    addressError.value = '';

    try {
        await ApiService.apiCall(
            '/travel-plus/billing-address',
            'put',
            addressForm.value
        );

        emit('address-saved', addressForm.value);
        emit('update:show', false);
    } catch (e) {
        addressError.value =
            e?.response?.data?.message ||
            'Something went wrong. Please try again.';
    } finally {
        addressProcessing.value = false;
    }
};
</script>
